/* eslint-disable max-lines */

import { NgTemplateOutlet } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Injector,
  Input,
  OnChanges,
  OnDestroy,
  Optional,
  Output,
  runInInjectionContext,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from '@angular/core';

import { PushPipe } from '@ngrx/component';
import { Subject, Subscription } from 'rxjs';
import { first, map, takeUntil } from 'rxjs/operators';

import {
  filterBoolean,
  RtComponentOutlet,
  StaticInputsComponent,
  TaskPriority,
  TaskScheduler,
} from '@recruitee/common';
import { asViewRef } from '@recruitee/utils';

import { IconColorDirective } from '../../common/icon/directives/icon-color.directive';
import { IconComponent } from '../../common/icon/icon.component';
import { PlaceloaderComponent } from '../../common/placeloader/placeloader.component';
import { CheckboxComponent } from '../../forms/checkbox/checkbox.component';
import { IntersectionObserverArea } from '../../intersection-observer/intersection-observer-area';
import { ExpandableCellService } from '../cells/expandable-cell/expandable-cell.service';
import {
  DataTableCellContext,
  DataTableRow,
  DataTableSelectionCellContext,
  TableCellFactory,
  TableCellFactoryResult,
} from '../data-table.types';

@Component({
  selector: 'rt-data-table-row',
  template: `
    <ng-container
      *ngTemplateOutlet="alwaysVisible || visible ? visibleTpl : hiddenTpl"
    ></ng-container>

    <ng-template #visibleTpl>
      <tr>
        @if (selectable) {
          <td
            (click)="!disabled && rowSelect.emit(!selected); $event.stopPropagation()"
            [attr.data-cy]="'datatable:row:' + index + ':cell:select'"
            class="selection-cell"
          >
            <div class="cell-body">
              @if (selectionCellTemplate) {
                <ng-container
                  *ngTemplateOutlet="selectionCellTemplate; context: selectionCellContext"
                ></ng-container>
              }
              @if (!selectionCellTemplate) {
                <rt-checkbox
                  [disabled]="disabled"
                  [checked]="selected"
                  (valueChange)="rowSelect.emit(!!$event)"
                >
                </rt-checkbox>
              }
            </div>
          </td>
        }
        @for (
          columnId of columnIds;
          track trackByColumnId(colIndex, columnId);
          let colIndex = $index
        ) {
          <td
            class="data-cell"
            [attr.data-cy]="'datatable:row:' + (index + 1) + ':cell:' + columnId"
            (click)="rowDetailsTemplate && toggleDetailsRow()"
          >
            <div class="cell-body">
              @if (!cellFactories[columnId]) {
                <ng-container
                  [ngTemplateOutlet]="cellTemplateMap[columnId] || defaultCellTemplate"
                  [ngTemplateOutletContext]="cellsContexts[columnId]"
                />
              }
              @if (cells[colIndex] | ngrxPush; as cell) {
                <ng-container
                  [rtComponentOutlet]="cell?.component ?? null"
                  [rtComponentOutletInputs]="cell?.inputs"
                  [rtComponentOutletOutputs]="cell?.outputs"
                />
              }
            </div>
          </td>
        }
        @if (rowDetailsTemplate) {
          <td (click)="toggleDetailsRow()" class="cursor-pointer">
            <div class="cell-body">
              <rt-icon
                [name]="detailsVisible ? 'keyboard-arrow-up' : 'keyboard-arrow-down'"
              ></rt-icon>
            </div>
          </td>
        }
      </tr>
      @if (rowDetailsTemplate && detailsVisible) {
        <tr>
          <td
            [attr.colspan]="columnIds.length + (selectable ? 1 : 0) + 1"
            class="no-padding details-row"
          >
            <ng-container
              [ngTemplateOutlet]="rowDetailsTemplate"
              [ngTemplateOutletContext]="{ row: this.row }"
            >
            </ng-container>
          </td>
        </tr>
      }
    </ng-template>

    <ng-template #hiddenTpl>
      <tr #hiddenEl>
        <td
          [attr.colspan]="columnIds.length + (selectable ? 1 : 0)"
          [style.verticalAlign]="'middle'"
          [style.padding]="'1rem'"
          [style.height]="notVisibleItemHeight"
        >
          <rt-placeloader
            [style.height]="'100%'"
            [style.width]="'30%'"
            class="disable-animations"
          ></rt-placeloader>
        </td>
      </tr>
    </ng-template>
  `,
  styleUrls: ['./data-table-row.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.selected]': 'selected',
    '[class.not-selected]': '!selected',
    '[class.selectable]': 'selectable',
    '[class.clickable]': 'clickable',
    '[class.private]': 'private',
    '[class.hoverable]': 'hoverable',
    '[class.cellPadding]': 'cellPadding',
    '[class.is-zebra]': 'isZebra',
    '[class.disabled]': 'disabled',
  },
  imports: [
    CheckboxComponent,
    IconColorDirective,
    IconComponent,
    NgTemplateOutlet,
    PlaceloaderComponent,
    RtComponentOutlet,
    PushPipe,
  ],
  providers: [ExpandableCellService],
})
export class DataTableRowComponent<Row extends DataTableRow>
  implements OnChanges, AfterViewInit, OnDestroy
{
  @Input() public index: number;
  @Input() public row: Row;
  @Input() public columnIds: string[];
  @Input() public cellTemplates: { [key: string]: TemplateRef<DataTableCellContext> } | undefined;
  @Input() public cellFactories: Record<string, TableCellFactory<Row> | null>;
  @Input() public defaultCellTemplate: TemplateRef<DataTableCellContext>;
  @Input() public rowDetailsTemplate: TemplateRef<any>;
  @Input() public hoverable: boolean = false;
  @Input() public clickable: boolean = false;
  @Input() public selectable: boolean = false;
  @Input() public selectionCellTemplate:
    | TemplateRef<DataTableSelectionCellContext>
    | null
    | undefined;
  @Input() public selected: boolean = false;
  @Input() public private: boolean = false;
  @Input() public even: boolean = false;
  @Input() public cellPadding: boolean = true;
  @Input() public isZebra: boolean = true;
  @Input() public disabled: boolean = false;

  @Input() public alwaysVisible: boolean = true;
  @Input() public notVisibleItemHeight: string = '40px';

  @Output() public rowSelect: EventEmitter<boolean> = new EventEmitter();

  public visible: boolean = false;
  public cellTemplateMap: Record<string, TemplateRef<DataTableCellContext>> = {};
  public detailsVisible: boolean = false;
  public cells: Array<TableCellFactoryResult<StaticInputsComponent> | undefined> = [];

  public selectionCellContext: DataTableSelectionCellContext | null;
  public cellsContexts: { [key: string]: DataTableCellContext<Row> } = {};

  @ViewChild('hiddenEl') public hiddenEl: ElementRef;
  private ngOnDestroy$ = new Subject<void>();
  private areaSub: Subscription | null;

  constructor(
    @Optional() private area: IntersectionObserverArea,
    private elRef: ElementRef,
    private cdRef: ChangeDetectorRef,
    private taskScheduler: TaskScheduler,
    private injector: Injector,
  ) {}

  // eslint-disable-next-line max-lines-per-function
  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.alwaysVisible) {
      if (this.alwaysVisible || !this.area?.serviceAvailable) {
        this.visible = true;
      } else {
        this.drawWhenVisible();
      }
    }

    if (changes.row || changes.columnIds || changes.cellTemplates) {
      this.cellTemplateMap = this.getCellTemplates();
      this.cellsContexts = this.columnIds.reduce(
        (acc, columnId, cellIndex) => {
          const cellContext =
            !changes.row && this.cellsContexts[columnId]
              ? this.cellsContexts[columnId]
              : {
                  columnId,
                  row: this.row,
                  rowIndex: this.index,
                  rowEven: this.even,
                  cellIndex,
                };

          acc[columnId] = cellContext;
          return acc;
        },
        <Record<string, DataTableCellContext<Row>>>{},
      );
    }

    if (changes.selected && this.selectable && this.selectionCellTemplate) {
      this.selectionCellContext = {
        row: this.row,
        selected: this.selected,
        handleSelect: selected => this.rowSelect.emit(selected),
      };
    }

    if (changes.row || changes.cellFactories || changes.columnIds) {
      runInInjectionContext(this.injector, () => {
        this.cells = this.columnIds.map(columnId =>
          this.cellFactories[columnId]?.(this.cellsContexts[columnId]),
        );
      });
    }
  }

  public ngAfterViewInit() {
    this.drawWhenVisible();
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public trackByColumnId(index: number, columnId: string): string {
    return columnId;
  }

  public toggleDetailsRow(): void {
    this.detailsVisible = !this.detailsVisible;
    this.cdRef.detectChanges();
  }

  private drawWhenVisible() {
    if (!this.area?.serviceAvailable || !this.hiddenEl) return;

    if (this.areaSub) {
      this.areaSub.unsubscribe();
      this.areaSub = null;
    }

    this.areaSub = this.area
      .intersectionChange(this.hiddenEl)
      .pipe(
        map(({ visible }) => visible),
        filterBoolean,
        first(),
        takeUntil(this.ngOnDestroy$),
      )
      .subscribe({
        next: () => this.show(),
        error: () => this.show(),
      });
  }

  private getCellTemplates() {
    return this.columnIds.reduce<Record<string, TemplateRef<DataTableCellContext>>>(
      (acc, columnId) => {
        const template =
          this.cellTemplates && this.cellTemplates[columnId]
            ? this.cellTemplates[columnId]
            : this.defaultCellTemplate;

        acc[columnId] = template;
        return acc;
      },
      {},
    );
  }

  private show(): void {
    this.taskScheduler.scheduleMacrotask(TaskPriority.High, () => {
      if (asViewRef(this.cdRef).destroyed || this.visible) return;

      this.visible = true;
      this.cdRef.detectChanges();
    });
  }
}
